/**
 * AccountCard — phone (read-only V1.0.x), email (editable), recovery code.
 *
 * Section content only (no outer Card wrapper). Each row shares a
 * 3-column shape:
 *   [LABEL + meta]  [VALUE / description]  [ACTION button]
 *
 * Disabled actions carry a `title` tooltip explaining why so reviewers
 * and users can see at a glance which surfaces are intentionally
 * stubbed for V1.x.
 */
import { Lock, Pencil, KeyRound, ShieldCheck } from "lucide-react";
import { Button, StatusPill } from "@flint/ui";
import type { MeUser } from "../../lib/session.ts";
import { formatPhoneIN } from "../../lib/format.ts";

export interface AccountCardProps {
  user: MeUser;
  onEditEmail: () => void;
  onVerifyEmail: () => void;
}

export function AccountCard({ user, onEditEmail, onVerifyEmail }: AccountCardProps) {
  return (
    <section>
      <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-3">
        <h2 className="text-sm font-semibold text-ink uppercase tracking-wider">Account</h2>
        <p className="text-sm text-muted mt-1">
          Your sign-in credentials and recovery options.
        </p>
      </div>

      {/* Rows divided by a hairline so each one reads as its own
          settable. Last row drops the divider. */}
      <Row
        label="Phone"
        meta={
          <span className="inline-flex items-center gap-1 text-sm text-muted">
            <Lock size={12} strokeWidth={2} aria-hidden />
            Credential
          </span>
        }
        value={<span className="num text-ink">{formatPhoneIN(user.phone)}</span>}
        action={
          <Button
            intent="ghost"
            size="sm"
            disabled
            title="Phone change requires step-up OTP — wires up with identity.recovery (V1.x)."
          >
            Change
          </Button>
        }
      />

      <Row
        label="Email"
        value={
          user.email ? (
            <span className="inline-flex items-center gap-2 min-w-0">
              <span className="text-ink truncate">{user.email}</span>
              {user.emailVerifiedAt ? (
                <StatusPill tone="ok" size="xs">
                  verified
                </StatusPill>
              ) : (
                <StatusPill tone="warn" size="xs">
                  unverified
                </StatusPill>
              )}
            </span>
          ) : (
            <span className="text-muted">Not set</span>
          )
        }
        action={
          <span className="inline-flex items-center gap-1">
            {user.email && !user.emailVerifiedAt ? (
              <Button intent="primary" size="sm" onClick={onVerifyEmail}>
                <ShieldCheck size={14} strokeWidth={2} aria-hidden />
                Verify email
              </Button>
            ) : null}
            <Button intent="ghost" size="sm" onClick={onEditEmail}>
              <Pencil size={14} strokeWidth={2} aria-hidden />
              Edit
            </Button>
          </span>
        }
      />

      <Row
        label="Recovery code"
        value={<span className="text-sm text-muted">Used if you lose access to your phone.</span>}
        action={
          <Button
            intent="ghost"
            size="sm"
            disabled
            title="Recovery rotation lands with identity.recovery (V1.x)."
          >
            <KeyRound size={14} strokeWidth={2} aria-hidden />
            Rotate
          </Button>
        }
        isLast
      />
    </section>
  );
}

interface RowProps {
  label: string;
  meta?: React.ReactNode;
  value: React.ReactNode;
  action: React.ReactNode;
  isLast?: boolean;
}

function Row({ label, meta, value, action, isLast }: RowProps) {
  return (
    <div
      className={`px-5 sm:px-6 py-4 flex items-start gap-4 ${isLast ? "" : "border-b border-line"}`}
    >
      <div className="min-w-0 flex-1 flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted uppercase tracking-wider">{label}</span>
          {meta}
        </div>
        <div className="text-sm">{value}</div>
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  );
}
